import { Search, RefreshCw, TrendingUp, LogOut, Settings } from 'lucide-react';
import { CRMStats } from '@/types/crm';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface CRMHeaderProps {
  stats: CRMStats | null;
  onSearch: (term: string) => void;
  onRefresh: () => void;
  refreshing: boolean;
  userEmail?: string;
  onLogout?: () => void;
  onOpenSettings?: () => void;
}

export const CRMHeader = ({ 
  stats, 
  onSearch, 
  onRefresh, 
  refreshing,
  userEmail,
  onLogout,
  onOpenSettings
}: CRMHeaderProps) => {
  return (
    <header className="glass-strong border-b border-border sticky top-0 z-50">
      <div className="max-w-[1600px] mx-auto px-6 py-5">
        {/* Logo, Título e User Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-primary w-12 h-12 rounded-xl flex items-center justify-center shadow-glow">
              <TrendingUp size={28} className="text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                CRM Galaxia
              </h1>
              <p className="text-sm text-muted-foreground">
                Gestão de Atendimentos WhatsApp
              </p>
            </div>
          </div>
          
          {userEmail && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground hidden sm:block">
                {userEmail}
              </span>
              {onOpenSettings && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onOpenSettings}
                  className="border-border hover:bg-card"
                >
                  <Settings className="w-4 h-4" />
                </Button>
              )}
              {onLogout && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLogout}
                  className="border-border hover:bg-card"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <div className="flex gap-6 py-3 border-y border-border/50 my-4 overflow-x-auto">
            <div className="flex flex-col gap-1 shrink-0">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                TOTAL DE LEADS
              </span>
              <span className="text-xl font-bold text-foreground">
                {stats.total}
              </span>
            </div>
            {Object.entries(stats.byStage).slice(0, 4).map(([stage, count]) => (
              <div key={stage} className="flex flex-col gap-1 shrink-0">
                <span className="text-xs text-muted-foreground uppercase tracking-wider truncate max-w-[120px]">
                  {stage}
                </span>
                <span className="text-xl font-bold text-foreground">
                  {count}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Busca e Ações */}
        <div className="flex gap-3 items-center">
          <div className="flex-1 max-w-md relative flex items-center gap-2.5 glass rounded-lg px-4">
            <Search size={18} className="text-primary" />
            <input
              type="text"
              placeholder="Buscar por nome, telefone, produto..."
              onChange={(e) => onSearch(e.target.value)}
              className="flex-1 bg-transparent border-none py-2.5 text-sm outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>

          <button
            onClick={onRefresh}
            disabled={refreshing}
            className={cn(
              "flex items-center gap-2 px-4 py-2.5 bg-gradient-primary text-primary-foreground rounded-lg text-sm font-semibold transition-all duration-200",
              refreshing ? "opacity-60 cursor-not-allowed" : "hover:shadow-glow hover:scale-105"
            )}
          >
            <RefreshCw size={18} className={cn(refreshing && "animate-spin-slow")} />
            Atualizar
          </button>
        </div>
      </div>
    </header>
  );
};